import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import moment from "moment";

// Listing state for the PAN Vendor Data report (generated PAN forms). Same
// shape as PanFormReportSlice — the two reports are the two halves of the same
// queue — but its own slice so filtering one never moves the other.

export interface PanVendorDataSliceStateType {
  items: any[];
  totalItems: number;
  isTableLoading: boolean;
  page: number;
  rowsPerPage: number;
  searchValue: string;
  filterBy: {
    fieldName: string;
    value: string[];
  }[];
  dateFilter: {
    start_date: string | null | any;
    end_date: string | null | any;
  };
  orderByValue: number;
}

/**
 * How many days of generated forms the report opens on — today and the two
 * days before it. It is also the furthest back a sub-admin (`ADMIN`) may pick
 * in the date filter; SUPER_ADMIN keeps the full range. The API applies the
 * same limit to sub-admins (pan-form.service `limitSubAdminDateFilter`).
 */
export const PAN_VENDOR_DATA_DEFAULT_DAYS = 3;

/** Earliest date of the default range, as `yyyy-MM-DD`. */
export const getPanVendorDataMinDate = () =>
  moment()
    .subtract(PAN_VENDOR_DATA_DEFAULT_DAYS - 1, "days")
    .format("yyyy-MM-DD");

// Built on call rather than once at import, so a tab left open past midnight
// still gets "the last 3 days" when the screen is reopened (resetState).
export const getPanVendorDataDefaultDateFilter = () => ({
  start_date: getPanVendorDataMinDate(),
  end_date: moment().format("yyyy-MM-DD"),
});

const initialState: PanVendorDataSliceStateType = {
  items: [],
  totalItems: 0,
  isTableLoading: false,
  page: 1,
  rowsPerPage: 10,
  searchValue: "",
  filterBy: [
    { fieldName: "status", value: [] },
    { fieldName: "applicationType", value: [] },
    { fieldName: "distributorCode", value: [] },
    // The centre the acknowledgement number was issued against — matched as a
    // prefix of it by the API, not as a field of its own. See
    // pan-form.service.ts `generatedFilters`.
    { fieldName: "reqBranchCode", value: [] },
  ],
  dateFilter: getPanVendorDataDefaultDateFilter(),
  orderByValue: -1,
};

const PanVendorDataSlice: any = createSlice({
  name: "panVendorData",
  initialState,
  reducers: {
    resetState: () => ({
      ...initialState,
      dateFilter: getPanVendorDataDefaultDateFilter(),
    }),
    setItems: (state, action: PayloadAction<any[]>) => {
      state.items = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
      document.getElementById("scroll-top")?.scrollTo(0, 0);
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.rowsPerPage = action.payload;
      state.page = 1;
      document.getElementById("scroll-top")?.scrollTo(0, 0);
    },
    setSearchValue: (state, action: PayloadAction<string>) => {
      state.searchValue = action.payload;
      state.page = 1;
    },
    setTotalItems: (state, action: PayloadAction<number>) => {
      state.totalItems = action.payload;
    },
    setIsTableLoading: (state, action: PayloadAction<boolean>) => {
      state.isTableLoading = action.payload;
    },
    setFilterBy: (
      state,
      action: PayloadAction<{ fieldName: string; value: string[] }[]>
    ) => {
      state.filterBy = action.payload;
      state.page = 1;
    },
    setDateFilter: (
      state,
      action: PayloadAction<{ start_date: string; end_date: string }>
    ) => {
      state.dateFilter = action.payload;
      state.page = 1;
    },
    setOrderByValue: (state, action: PayloadAction<number>) => {
      state.orderByValue = action.payload;
    },
  },
});

export const {
  resetState,
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setTotalItems,
  setIsTableLoading,
  setFilterBy,
  setDateFilter,
  setOrderByValue,
} = PanVendorDataSlice.actions;
export default PanVendorDataSlice.reducer;
